// Transcription Panel Component
import React, { useEffect, useRef, useState } from 'react';
import { MessageSquare, Clock, Trash2, Copy, Check } from 'lucide-react';
import { Transcription } from '../../types';
import { formatTime } from '../../utils/helpers';

interface TranscriptionPanelProps {
  transcriptions: Transcription[];
  isActive: boolean;
  onClear: () => void;
}

export const TranscriptionPanel: React.FC<TranscriptionPanelProps> = ({
  transcriptions,
  isActive,
  onClear
}) => {
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to top when new transcriptions arrive
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [transcriptions]);

  const handleCopyTranscription = async (text: string, id: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error('Failed to copy text:', err);
    }
  };


  return (
    <div className="h-full flex flex-col p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-200">
        <div className="flex items-center">
          <MessageSquare className="w-5 h-5 text-primary-600 mr-2" />
          <h2 className="text-lg font-semibold text-gray-900">
            Sent to LLM
          </h2>
          {isActive && (
            <div className="ml-3 flex items-center">
              <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse mr-1" />
              <span className="text-sm text-red-600 font-medium">LIVE</span>
            </div>
          )}
        </div>
        
        {transcriptions.length > 0 && (
          <button
            onClick={onClear}
            className="flex items-center px-3 py-1 text-gray-600 hover:text-gray-800 text-sm rounded hover:bg-gray-100 transition-colors"
            title="Clear all transcriptions"
          >
            <Trash2 className="w-4 h-4 mr-1" />
            Clear
          </button>
        )}
      </div>

      {/* Transcription List */}
      <div className="flex-1 overflow-hidden">
        {transcriptions.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-gray-500">
            <MessageSquare className="w-12 h-12 text-gray-300 mb-4" />
            <p className="text-center">
              {isActive 
                ? 'Speech sent to the LLM (auto or from the input bar) will appear here.' 
                : 'Start your interview to see LLM-bound messages here.'
              }
            </p>
          </div>
        ) : (
          <div 
            ref={scrollRef}
            className="h-full overflow-y-auto space-y-3 pr-2"
          >
            {transcriptions.filter(t => t && t.id && t.text).map((transcription) => (
              <div
                key={transcription.id}
                className="p-4 rounded-lg border transition-all duration-200 bg-primary-50 border-primary-200"
              >
                {/* Header */}
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium bg-primary-600 text-white">
                      Q
                    </div>
                    <span className="text-sm font-medium text-gray-900">
                      {transcription.source === 'chatbot' ? 'Your question' : 'Interview speech'}
                    </span>
                    <div className="flex items-center text-xs text-gray-500">
                      <Clock className="w-3 h-3 mr-1" />
                      {formatTime(transcription.timestamp)}
                    </div>
                  </div>
                  
                  {/* Copy Button */}
                  <button
                    onClick={() => handleCopyTranscription(transcription.text || '', transcription.id)}
                    className="p-1 text-gray-400 hover:text-gray-600 rounded transition-colors"
                    title="Copy transcription"
                  >
                    {copiedId === transcription.id ? (
                      <Check className="w-3 h-3 text-green-600" />
                    ) : (
                      <Copy className="w-3 h-3" />
                    )}
                  </button>
                </div>

                {/* Transcription Text */}
                <p className="text-gray-800 text-sm leading-relaxed">
                  {transcription.text || 'No text available'}
                </p>

                {/* Question Indicator */}
                <div className="mt-2 flex items-center text-xs text-primary-700">
                  <div className="w-1 h-1 bg-primary-600 rounded-full mr-2" />
                  Sent to LLM
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer Stats */}
      {transcriptions.length > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="flex justify-between text-sm text-gray-600">
            <span>{transcriptions.length} message{transcriptions.length !== 1 ? 's' : ''} sent</span>
            <span>
              {transcriptions.length} LLM request{transcriptions.length !== 1 ? 's' : ''}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};
